/* app/pipeline-todo.jsx - To-do aggregated action hub, wired to
   /api/research/todo-hub (items + resolve), scoped to the active site.
   Add-to-do form creates manual todos via POST /api/todos (title required,
   backend/routes/todos.js); they come back through the hub feed on reload.

   TWO LISTS, AND THE QUEUE NOW HAS A POSITION TOO.

   The ACTION QUEUE is the hub: /research/todo-hub builds eleven kinds of
   pending work. It used to answer for ONE CONNECTION at every position, and
   that was this screen's loudest live defect: on the live property 219 items
   sat on one blog, and standing anywhere else the queue was empty, so the
   switcher's "the whole website" position showed a customer none of their own
   work (docs/MODULE-SCOPE.md 8.6).

   It now takes the position, and the eleven kinds do NOT all widen, because
   their subjects do not all belong to the same thing:

     the whole website   to-dos and captured gaps, inbox items, page targets.
                         Identity tables, and a to-do about the marketing front
                         end has no section to live in.
     the whole market    topics and keywords. A register is per market
                         (PROPERTY-MODEL 3.13), and one market shares one.
     this connection     the Business Profile, entities awaiting validation,
                         entities with no URL, offering gaps, due re-analyses
                         and the "never analysed" list. All six read bi_entities,
                         the live property holds one Business Profile per
                         section inside one market, and the app refuses on two
                         rather than guessing. Widening them would make every AI
                         run refuse on real customer data (PROPERTY-MODEL 3.4c).

   SO THE SCREEN SAYS WHICH IS WHICH, from the server's own answer rather than
   from a list kept here: `scope.kinds` names all eleven, what each one answered
   at, and how many it holds, counted from the items this request built. A queue
   that covered five kinds of eleven in silence would read as complete.

   The TO-DO LIST is /todos, which step 5 widened to the whole website with the
   switcher position arriving as an optional section_id. To-do is "property,
   filtered" (docs/MODULE-SCOPE.md 3) for a reason the table states in one
   line: a to-do about the marketing front end has no section to live in if
   sections are scopes. It AGGREGATES, so /todos/summary's four counts are
   arithmetic over one set and the per section breakdown is on screen beside
   them at All. The queue below it ENUMERATES (1.1): every row names its
   section and offers no total. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }

  const SOURCE_ICON = { Competitors: "flag", Partners: "git-branch", Industry: "globe", Products: "box", Inbox: "inbox", Research: "target", Manual: "edit" };
  const PRIO_COLOR = { high: "research-prio--high", medium: "research-prio--medium", low: "research-prio--low" };
  // backend source (entity_type/etc) → prototype source label (drives icon + nav)
  const SRC_MAP = { competitor: "Competitors", partner: "Partners", industry: "Industry", product: "Products", inbox: "Inbox", research: "Research", manual: "Manual", page: "Research", profile: "Manual" };
  function mapHub(it) {
    return {
      id: it.uid, title: it.title || "", type: it.type || "", source: SRC_MAP[it.source] || "Manual",
      pri: it.priority && it.priority !== "none" ? it.priority : "low",
      status: it.status === "in_progress" ? "progress" : (it.status || "pending"),
      sub: it.subtitle || "", actions: it.actions || [],
      nav: it.kind !== "todo" && it.kind !== "profile",
      _kind: it.kind, _bid: it.id,
      // THE ROW'S OWN CONNECTION, which is what every write about it is
      // addressed to. The queue can list a row of another section, and
      // /todo-hub/resolve matches `id = ? AND site_id = ?`: sending the
      // switcher's anchor instead would match nothing while the request
      // succeeded, which is the "that to-do is gone" defect MODULE-SCOPE 8.6
      // records against DELETE /todos/:id.
      _site: it.site_id == null ? null : Number(it.site_id),
      // The section it sits in, and the two per row verbs (MODULE-SCOPE 1.2).
      // `resolvable` is the server's, not a set kept here: which kinds the
      // resolver can act on is that file's switch statement, and a second
      // opinion about it is how a screen comes to offer a button that 400s.
      section: it.section || null,
      elsewhere: it.elsewhere === true,
      resolvable: it.resolvable !== false,
      resolveWhy: it.resolve_why || null,
      openWhy: it.open_why || null,
    };
  }

  const ACTION = {
    approve: ["check", "Approve", "primary"], dismiss: ["x", "Dismiss", ""], done: ["check", "Done", "primary"],
    start: ["play", "Start", ""], progress: ["play", "Start", ""], reopen: ["rotate-ccw", "Reopen", ""], open: ["arrow-up-right", "Open", ""],
  };

  // Kinds POST /research/todo-hub/resolve accepts; every other kind (stale,
  // coverage, missing_url, offering_gap, profile) gets 400 "invalid kind",
  // so select mode must not offer them for bulk work.
  //
  // KEPT AS THE FALLBACK ONLY. Each row now carries `resolvable` from the
  // server, which is the one place that knows what its own resolver accepts;
  // this set answers for a payload served before that field existed, so a
  // cached bundle does not lose its checkboxes.
  const RESOLVABLE = new Set(["todo", "topic", "keyword", "entity", "inbox", "page"]);
  const canResolve = (it) => (it && it.resolvable !== undefined ? it.resolvable : RESOLVABLE.has(it && it._kind));
  // What each bulk bucket may truthfully send per kind. The resolver treats
  // ANY non-dismiss action on topic/keyword/entity as approve, "dismiss" on a
  // todo as reopen-to-open, and unknown actions on inbox/page as silent
  // no-ops - so a bucket sends only what the kind genuinely does; a kind
  // absent from its bucket is skipped and counted, never sent blind.
  const BULK_ACTION = {
    progress: { todo: "progress" },
    done: { todo: "done", page: "done", topic: "approve", keyword: "approve", entity: "approve" },
    dismiss: { topic: "dismiss", keyword: "dismiss", entity: "dismiss", inbox: "dismiss", page: "dismiss" },
  };

  // Manual to-do creation. POST /api/todos needs site_id + non-empty title;
  // priority/due_date/notes are optional (backend/routes/todos.js validates
  // and defaults them). On success the parent reloads the hub feed, the same
  // path a resolve takes, so the new row appears without a page refresh.
  // `siteId` is the ANCHOR, which is the connection a new to-do is filed
  // against, and that is the section the switcher is standing on whenever it
  // names one. At All it is one piece of the website out of several, so the
  // form says which rather than leaving a person to find out from the Section
  // column after saving.
  function TodoForm({ siteId, siteLabel, whole, onClose, onCreated }) {
    const DS = window.HuddleDesignSystem_775113; const { Input, Textarea, Select } = DS;
    const [title, setTitle] = useState("");
    const [notes, setNotes] = useState("");
    const [priority, setPriority] = useState("medium");
    const [dueDate, setDueDate] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const save = async () => {
      if (!title.trim()) { setError("Title is required."); return; }
      if (siteId == null) { setError("Select a site before adding a to-do."); return; }
      setSaving(true); setError("");
      try {
        await api(`/todos`, { method: "POST", body: JSON.stringify({ site_id: siteId, title: title.trim(), notes: notes.trim() || undefined, priority, due_date: dueDate || undefined }) });
        // Reload first, then close: the form unmounts on close, so no state
        // writes may happen after it (avoids a set-state-on-unmounted warning).
        if (onCreated) await onCreated();
        onClose();
      } catch (err) {
        setError((err && err.message) || "Could not save the to-do.");
        setSaving(false);
      }
    };
    return (
      <div className="todo-add-form">
        <div className="bp-grid">
          <div className="bp-full"><label className="bi-field__label">Title</label><Input placeholder="What needs doing?" value={title} onChange={e => setTitle(e.target.value)} /></div>
          <div className="bp-full"><label className="bi-field__label">Notes</label><Textarea rows={2} placeholder="Optional details" value={notes} onChange={e => setNotes(e.target.value)} /></div>
          <div><label className="bi-field__label">Priority</label><Select value={priority} onChange={e => setPriority(e.target.value)}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option><option value="none">None</option></Select></div>
          <div><label className="bi-field__label">Due date</label><Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} /></div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 14 }}>
          {error ? <span style={{ fontSize: 12, color: "var(--danger)", marginRight: "auto" }}>{error}</span>
            : whole && siteLabel ? <span style={{ fontSize: 12, color: "var(--text-muted)", marginRight: "auto" }}>Filed against {siteLabel}.</span>
            : null}
          <button className="bi-btn bi-btn--sm" disabled={saving} onClick={onClose}>Cancel</button>
          <button className="bi-btn bi-btn--sm bi-btn--primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    );
  }

  // dropdown filter pill (label + active value + count menu) - reuses bi-filterbar
  function FilterPill({ label, value, opts, counts, onPick }) {
    const [open, setOpen] = useState(false);
    const cur = opts.find(o => o[0] === value);
    const active = value !== "all";
    return (
      <div className={"bi-filterpill" + (active ? " bi-filterpill--active" : "")}>
        <button className="bi-filterpill__btn" onClick={() => setOpen(o => !o)}>
          {label}{active && <>:&nbsp;<span className="bi-filterpill__val">{cur[1]}</span></>}
          {active ? <span className="bi-filterpill__x" onClick={e => { e.stopPropagation(); onPick("all"); }}><I name="x" size={13} /></span> : <I name="chevron-down" size={14} />}
        </button>
        {open && <>
          <div style={{ position: "fixed", inset: 0, zIndex: 29 }} onClick={() => setOpen(false)} />
          <div className="bi-filtermenu">
            {opts.map(([id, l]) => (
              <button key={id} className="bi-filtermenu__item" onClick={() => { onPick(id); setOpen(false); }}>
                {l}{counts && counts[id] != null && <span className="bi-filtermenu__n">{counts[id]}</span>}{value === id && <span className="bi-filtermenu__check"><I name="check" size={15} /></span>}
              </button>
            ))}
          </div>
        </>}
      </div>
    );
  }

  // ---- the website's own to-dos: GET /todos + GET /todos/summary ----
  // The counts are the summary's, which counts rows in the database over the
  // position the switcher names. They are never taken from the list below:
  // "N open" has to be true about the website and not about however many rows
  // this screen happens to be holding.
  //
  // One line until it is opened, because the action queue is what this screen
  // leads with and this is the second list, not a second screen.
  const TODO_COLS = [["open", "Open"], ["in_progress", "In progress"], ["done", "Done"], ["urgent", "Urgent"]];
  const TODO_STATUS = { open: "Open", in_progress: "In progress", done: "Done" };
  // Which site a row belongs to. `site_id` is the column DELETE matches on and
  // the server copies it into `section_id` on the way out, so either answers;
  // the first is the one the route names.
  const rowSiteOf = (t) => (t && t.site_id != null ? t.site_id : (t && t.section_id != null ? t.section_id : null));
  function TodoList({ whole, where, state, rows, summary, err, summaryErr, onRetry, onStatus, onDelete, rowErr }) {
    const [open, setOpen] = useState(false);
    const [armed, setArmed] = useState(null);     // the row id whose Delete is one click from firing
    const counts = summary || {};
    const secs = (summary && Array.isArray(summary.sections)) ? summary.sections : [];
    const un = (summary && summary.unattributed) || null;
    const unTotal = un ? (Number(un.total) || 0) : 0;
    const cell = { padding: "5px 10px", fontSize: 12, textAlign: "right", color: "var(--text-secondary)", whiteSpace: "nowrap" };
    const head = { ...cell, fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "var(--tracking-wide)" };
    const nameCell = { ...cell, textAlign: "left", color: "var(--text-primary)" };
    return (
      <div className="bi-card" style={{ marginBottom: 12, padding: 0, overflow: "hidden" }}>
        <button className="bi-filterpill__btn" style={{ width: "100%", justifyContent: "space-between", padding: "10px 12px" }} onClick={() => setOpen(o => !o)}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: "var(--text-sm)" }}>
            <I name="check-square" size={14} />
            To-dos across {where}
            {summary
              ? <span style={{ color: "var(--text-muted)" }}>{TODO_COLS.map(([k, l]) => `${l} ${Number(counts[k]) || 0}`).join(" · ")}</span>
              : (summaryErr || err) ? <span style={{ color: "var(--warning)" }}>the counts could not be read</span>
              : <span style={{ color: "var(--text-muted)" }}>counting…</span>}
          </span>
          <I name={open ? "chevron-down" : "chevron-right"} size={14} />
        </button>
        {open && (
          <div style={{ borderTop: "1px solid var(--border-subtle)" }}>
            {(err || summaryErr) && (
              <div className="bi-hint" style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <I name="alert-triangle" size={14} style={{ color: "var(--warning)" }} />
                {err ? `These to-dos could not be read: ${err}` : `The counts and the per piece breakdown could not be read, so the rows below are the only answer on screen: ${summaryErr}`}
                <button className="bi-btn bi-btn--sm" onClick={onRetry}>Retry</button>
              </div>
            )}
            {/* The parts the counts are made of. Every piece of the website
                keeps its row at zero, because emptiness is a state a module
                renders and never a reason to leave something out, and a total
                nobody can check is a total nobody should be shown. */}
            {whole && secs.length > 0 && (
              <div style={{ padding: "4px 2px 6px", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ ...head, textAlign: "left" }}>Piece of the website</th>
                      {TODO_COLS.map(([k, l]) => <th key={k} style={head}>{l}</th>)}
                      <th style={head}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {secs.map(s => (
                      <tr key={s.section_id} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                        <td style={nameCell}>
                          {s.name}
                          {s.can_publish === false && <span style={{ color: "var(--text-muted)" }}> · crawled, no CMS</span>}
                        </td>
                        {TODO_COLS.map(([k]) => <td key={k} style={cell}>{Number(s[k]) || 0}</td>)}
                        <td style={{ ...cell, fontWeight: 600, color: "var(--text-primary)" }}>{Number(s.total) || 0}</td>
                      </tr>
                    ))}
                    {/* Rows under a connection this website does not list are
                        counted apart rather than dropped, so the parts still
                        add to the whole. */}
                    {unTotal > 0 && (
                      <tr style={{ borderTop: "1px solid var(--border-subtle)" }}>
                        <td style={nameCell} title="Filed against a connection that is not one of this website's pieces, so it is counted here rather than dropped.">Not attributed to a piece</td>
                        {TODO_COLS.map(([k]) => <td key={k} style={cell}>{Number(un[k]) || 0}</td>)}
                        <td style={{ ...cell, fontWeight: 600, color: "var(--text-primary)" }}>{unTotal}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {summary.sections_why && <div className="bi-hint" style={{ padding: "8px 10px 0" }}>{summary.sections_why}</div>}
                {summary.read_filter && summary.read_filter !== "property_id" && summary.read_filter_why
                  && <div className="bi-hint" style={{ padding: "6px 10px 0" }}>{summary.read_filter_why}</div>}
              </div>
            )}
            {/* No pieces to break the counts down by is a state with a cause,
                and the cause is a connection that is not grouped under a
                property yet rather than an empty website. The server says
                which, so the screen shows it instead of an empty table. */}
            {whole && secs.length === 0 && summary && summary.read_filter_why
              && <div className="bi-hint" style={{ padding: "10px 12px 0" }}>{summary.read_filter_why}</div>}
            {state === "loading" ? <div className="bi-subempty" style={{ padding: 12 }}>Loading…</div>
              : rows.length === 0 ? <div className="bi-subempty" style={{ padding: 12 }}>No to-dos {whole ? "anywhere on this website" : "in this section"} yet.</div>
              : (
                <div className="bi-list--rows" style={{ borderTop: "1px solid var(--border-subtle)" }}>
                  {rows.map(t => (
                    <div className="bi-row" key={t.id}>
                      <div className="bi-row__main" style={{ flexBasis: 260 }}>
                        <span className="bi-row__name">{t.title}</span>
                        {t.notes && <span className="bi-row__url">{t.notes}</span>}
                      </div>
                      <div className="bi-row__badges">
                        <span className="todo-type">{TODO_STATUS[t.status] || t.status}</span>
                        <span className={"research-prio " + (PRIO_COLOR[t.priority] || "")}>{t.priority || "none"}</span>
                        {t.due_date && <span className="todo-type">due {String(t.due_date).slice(0, 10)}</span>}
                        {/* THE SECTION COLUMN. It is here at All, where the
                            list spans several pieces of the website, and it is
                            removed when the switcher names one, which is the
                            only thing that changes with it. */}
                        {whole && t.section_name && (
                          <span className="todo-source" title={(t.can_publish === false ? t.section_name + " is read by crawling it and has no CMS behind it. " : "")
                            + "The piece of the website this to-do is filed against"
                            + (t.section_not_derived_yet ? ", named from the connection it was created through because its section column has not been filled in yet." : ".")}>
                            <I name="layers" size={11} />{t.section_name}
                          </span>
                        )}
                      </div>
                      <div className="bi-row__actions">
                        {t.status !== "in_progress" && t.status !== "done" && <button className="bi-btn bi-btn--sm" onClick={() => onStatus(t, "in_progress")}><I name="play" size={14} />Start</button>}
                        {t.status !== "done"
                          ? <button className="bi-btn bi-btn--sm bi-btn--primary" onClick={() => onStatus(t, "done")}><I name="check" size={14} />Done</button>
                          : <button className="bi-btn bi-btn--sm" onClick={() => onStatus(t, "open")}><I name="rotate-ccw" size={14} />Reopen</button>}
                        {/* Delete is the one call here that names a site, so it
                            names THE ROW'S OWN. A row with no site cannot be
                            addressed at all, so the button says so instead of
                            sending the switcher's anchor and reading the 404 as
                            a missing to-do. */}
                        <button className="bi-btn bi-btn--sm bi-btn--danger" disabled={rowSiteOf(t) == null}
                                title={rowSiteOf(t) == null ? "This to-do does not say which connection holds it, so it cannot be deleted from here." : undefined}
                                onMouseLeave={() => setArmed(a => (a === t.id ? null : a))}
                                onClick={() => { if (armed !== t.id) { setArmed(t.id); return; } setArmed(null); onDelete(t); }}>
                          <I name="x" size={14} />{armed === t.id ? "Confirm delete" : "Delete"}
                        </button>
                        {rowErr[t.id] && <span style={{ fontSize: 11, color: "var(--danger)" }}>{rowErr[t.id]}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
      </div>
    );
  }

  /* WHAT IS AND IS NOT IN THE QUEUE, in one block, built from the server's
     `scope.kinds` and never from a list kept here.

     THE LINE THIS REPLACED said the queue was one connection's and the switcher
     did not filter it, which was true and is now false for five of the eleven
     kinds. A queue that covers five kinds of eleven and says nothing reads as
     complete, which is the failure this block exists to prevent: the owner's
     complaint was not only that the queue was narrow, it was that nothing on
     screen said so.

     Each group names its kinds and the LIVE count of items in them, so the
     sentence can be checked against the list under it rather than believed. The
     connection group carries the server's reason, because that one is the group
     a customer would otherwise read as missing data. */
  function ScopeNote({ scope, whole, anchorName, filtered }) {
    if (!scope || !Array.isArray(scope.kinds)) return null;
    const AT = {
      property  : { label: "the whole website", icon: "layers" },
      market    : { label: "the whole market",  icon: "globe" },
      connection: { label: anchorName ? `${anchorName} only` : "this connection only", icon: "git-branch" },
    };
    const order = ["property", "market", "connection"];
    const groups = order.map(at => {
      const ks = scope.kinds.filter(k => k.scope === at);
      if (!ks.length) return null;
      return {
        at,
        label: AT[at].label,
        icon: AT[at].icon,
        kinds: ks.map(k => k.label),
        // Counted from the items the request built, every kind added up: a
        // figure beside a sentence has to be the figure in the list.
        count: ks.reduce((a, k) => a + (k.count || 0), 0),
        // One reason per group, and only where there is one to give. The six
        // that stay at the connection share a single sentence, so taking the
        // first is taking the only one.
        why: (ks.find(k => k.why) || {}).why || null,
      };
    }).filter(Boolean);
    // At one section there is nothing to tell apart: every kind answered for
    // the connection, so the note is one sentence rather than a table.
    if (!whole) {
      return (
        <div className="bi-hint" style={{ display: "flex", alignItems: "flex-start", gap: 6, margin: "2px 0 8px" }}>
          <I name="git-branch" size={13} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>This queue is {anchorName || "this section"}'s own, all eleven kinds of it. Switch the website selector
            to the whole website to see the to-dos, captured gaps, inbox items, page targets, topics and keywords of
            every section.</span>
        </div>
      );
    }
    return (
      <div className="bi-hint" style={{ margin: "2px 0 8px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
          <I name="layers" size={13} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>This queue is not one scope, because the eleven kinds of work in it do not belong to one thing.
            Each row says which section it sits in.</span>
        </div>
        <ul style={{ margin: "6px 0 0", paddingLeft: 25, listStyle: "disc" }}>
          {groups.map(g => (
            <li key={g.at} style={{ marginTop: 2 }}>
              <b>{g.label}</b>{": "}{g.kinds.join(", ").toLowerCase()} ({g.count} {g.count === 1 ? "item" : "items"}
              {g.at === "connection" ? ", not filtered by the selector" : ""}).
              {g.at === "connection" && g.why ? <span style={{ color: "var(--text-muted)" }}> {g.why}</span> : null}
            </li>
          ))}
        </ul>
        {/* The counts above are of the QUEUE, and the filter bar below can hide
            rows from it. A figure beside a list that disagrees with it is the
            thing this block exists to avoid, so when a filter is on the note
            says which of the two it is counting. */}
        {filtered ? <div style={{ marginTop: 4, fontSize: 12, color: "var(--text-muted)" }}>Counted before the filters below, so the list can be shorter than these figures.</div> : null}
        {scope.why ? <div style={{ marginTop: 4, fontSize: 12, color: "var(--text-muted)" }}>{scope.why}</div> : null}
      </div>
    );
  }

  function TodoHub({ onNavigate, site, sectionId, sections }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    // The All position is `sectionId == null`, and it is the whole website. The
    // anchor still rides on every request, because it is what authorises it.
    const whole = sectionId == null;
    const sectionNode = whole ? null
      : ((sections || []).find(s => (s.site_id != null ? s.site_id : s.id) === sectionId) || null);
    const whereLabel = whole ? "this website" : ((sectionNode && sectionNode.name) || (site && site.name) || "this section");
    // The section is OMITTED at All rather than sent as a value meaning
    // everything: an absent filter and a filter naming every section are
    // different requests, and only the first asks what the website holds.
    const scoped = (path) => path + (whole ? "" : `&section_id=${sectionId}`);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [f, setF] = useState({ status: "all", type: "all", source: "all", pri: "all" });
    const setFilter = (k, v) => setF(s => ({ ...s, [k]: v }));
    const [viewMode, setViewMode] = useState("grid");
    const [selectMode, setSelectMode] = useState(false);
    const [sel, setSel] = useState([]);
    const toggle = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

    // THE HUB, AT THE POSITION THE SWITCHER NAMES.
    //
    // `section_id=all` is sent EXPLICITLY at All and is not simply omitted,
    // which is the one place this screen differs from the /todos calls above.
    // On that route an absent section means the whole website; on this one an
    // absent section means today's answer, because five other callers read this
    // same queue with no position (the console's per site card count, the
    // sidebar badge, the pipeline dashboard, the assistant's todo_hub action)
    // and none of them asked for the website. The screen with the switcher asks
    // by name, so nothing widens behind anyone's back.
    const [scope, setScope] = useState(null);
    const load = useCallback(async () => {
      if (siteId == null) { setItems([]); setScope(null); setLoading(false); return; }
      setLoading(true);
      try {
        const r = await api(`/research/todo-hub?site_id=${siteId}&section_id=${whole ? "all" : sectionId}`);
        setItems(((r && r.items) || []).map(mapHub));
        setScope((r && r.scope) || null);
      }
      catch { setItems([]); setScope(null); }
      finally { setLoading(false); }
    }, [siteId, sectionId]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    // THE WEBSITE'S OWN TO-DOS, at the position the switcher names. One
    // callback for both reads so the list and the counts above it can never be
    // answered at two different positions inside one render.
    const [todos, setTodos] = useState([]);
    const [todoSummary, setTodoSummary] = useState(null);
    const [todoState, setTodoState] = useState("loading");  // loading | ready | error
    const [todoErr, setTodoErr] = useState(null);
    const [todoSummaryErr, setTodoSummaryErr] = useState(null);
    const [todoRowErr, setTodoRowErr] = useState({});
    const loadTodos = useCallback(async () => {
      if (siteId == null) { setTodos([]); setTodoSummary(null); setTodoState("ready"); return; }
      setTodoState("loading"); setTodoErr(null); setTodoSummaryErr(null);
      // The summary is its own request and its own failure: a list that loads
      // must not be hidden because the arithmetic over it did not, and counts
      // that could not be read have to SAY so rather than sit at "counting"
      // for the rest of the session.
      api(scoped(`/todos/summary?site_id=${siteId}`))
        .then(s => { setTodoSummary(s); setTodoSummaryErr(null); })
        .catch(e => { setTodoSummary(null); setTodoSummaryErr((e && e.message) || "Request failed"); });
      try {
        const rows = await api(scoped(`/todos?site_id=${siteId}`));
        setTodos(Array.isArray(rows) ? rows : []);
        setTodoState("ready");
      } catch (e) { setTodos([]); setTodoErr((e && e.message) || "Request failed"); setTodoState("error"); }
    }, [siteId, sectionId]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { loadTodos(); }, [loadTodos]);

    // EVERY PER ROW CALL, CHECKED AGAINST A ROW OF ANOTHER SECTION, which is
    // the case All creates and a single section view never could.
    //
    // PATCH /todos/:id is addressed by the id alone (routes/todos.js), so it
    // reaches a row of any piece of this website.
    //
    // DELETE /todos/:id is NOT: it matches `id = ? AND site_id = ?`, so it is
    // sent THE ROW'S OWN site_id, which the row carries and the list shows.
    // Sending the switcher's anchor instead would 404 on every row belonging to
    // another section, and a 404 here reads as "that to-do is gone" rather than
    // as "the wrong site was named".
    //
    // Both reload the hub as well as this list, because a to-do of the anchor
    // connection appears in both and two lists of one row must not disagree
    // about its state.
    const afterTodoWrite = async () => { await Promise.all([loadTodos(), load()]); };
    const setTodoStatus = async (t, status) => {
      setTodoRowErr(e => { const n = { ...e }; delete n[t.id]; return n; });
      try { await api(`/todos/${t.id}`, { method: "PATCH", body: JSON.stringify({ status }) }); await afterTodoWrite(); }
      catch (err) { setTodoRowErr(e => ({ ...e, [t.id]: (err && err.message) || "Could not save that" })); }
    };
    const deleteTodo = async (t) => {
      const owner = rowSiteOf(t);
      if (owner == null) { setTodoRowErr(e => ({ ...e, [t.id]: "This to-do does not say which connection holds it" })); return; }
      setTodoRowErr(e => { const n = { ...e }; delete n[t.id]; return n; });
      try { await api(`/todos/${t.id}?site_id=${owner}`, { method: "DELETE" }); await afterTodoWrite(); }
      catch (err) { setTodoRowErr(e => ({ ...e, [t.id]: (err && err.message) || "Could not delete it" })); }
    };

    // Per-item action errors, keyed by hub uid: a failed resolve must show
    // next to its own buttons, not disappear into a swallowed catch.
    const [itemErr, setItemErr] = useState({});
    // Outcome line for the last bulk run ("N resolved, M skipped, K failed").
    const [bulkNotice, setBulkNotice] = useState("");
    // The item whose detail drawer is open. Clicking a card opens THIS, never
    // a whole module: jumping somewhere is a button inside the drawer, so the
    // click's cost is a panel, not a navigation.
    const [detail, setDetail] = useState(null);
    useEffect(() => {
      if (!detail) return;
      const onKey = (e) => { if (e.key === "Escape") setDetail(null); };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }, [detail]);
    // The referenced thing itself, where a single-item read exists: the hub
    // row only carries its own metadata, and a drawer about "Aksolot" must
    // show Aksolot (its site, its summary), not just "entity, pending".
    const [extra, setExtra] = useState(null);
    useEffect(() => {
      if (!detail || siteId == null) { setExtra(null); return; }
      const kind = detail._kind, id = detail._bid;
      // THE ROW'S OWN CONNECTION again, for the same reason the writes use it:
      // a keyword of another section read under the anchor is a 404, and a
      // drawer about "formation scrum" would then say its details could not be
      // read about a keyword that is right there in the list.
      const at = detail._site != null ? detail._site : siteId;
      const url = kind === "entity" ? `/bi/entities/${id}?site_id=${at}`
        : kind === "keyword" ? `/keywords/${id}?site_id=${at}`
        : kind === "inbox" ? `/inbox/${id}`
        : null;
      if (!url) { setExtra(null); return; }
      let cancel = false;
      setExtra({ loading: true });
      api(url).then(d => { if (!cancel) setExtra({ loading: false, data: d }); })
        .catch(e => { if (!cancel) setExtra({ loading: false, error: e.message || "The details could not be read." }); });
      return () => { cancel = true; };
    }, [detail && detail.id, siteId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Returns whether the action landed, so the drawer can close on success
    // and stay open showing the error when it did not. `kind` is on the
    // MAPPED item as _kind; it.kind does not exist here.
    //
    // THE ROW'S OWN CONNECTION is what every resolve is addressed to, and it
    // falls back to the anchor only for a row that did not carry one. An
    // "open" on a row that sits elsewhere does not navigate: the modules open
    // on the section the switcher is standing on, so the row says which section
    // to switch to instead of jumping somewhere that cannot show it.
    const resolve = async (it, action) => {
      if (action === "open") {
        if (it.elsewhere) { setItemErr(e => ({ ...e, [it.id]: it.openWhy || "This item belongs to another section." })); return false; }
        onNavigate && onNavigate(it.source, it._kind); return true;
      }
      const at = it._site != null ? it._site : siteId;
      if (at == null) return false;
      setItemErr(e => { const n = { ...e }; delete n[it.id]; return n; });
      try { await api(`/research/todo-hub/resolve`, { method: "POST", body: JSON.stringify({ site_id: at, kind: it._kind, id: it._bid, action }) }); await load(); return true; }
      catch (err) { setItemErr(e => ({ ...e, [it.id]: (err && err.message) || "Action failed" })); return false; }
    };
    const bulkResolve = async (bucket) => {
      if (siteId == null || !sel.length) return;
      const map = BULK_ACTION[bucket] || {};
      const chosen = items.filter(i => sel.includes(i.id));
      let ok = 0, skipped = 0, failed = 0; const failedIds = [];
      // One request per item, caught per item: a single 400 (an item the
      // resolver cannot handle, a race) must not silently abandon the rest.
      for (const it of chosen) {
        const action = map[it._kind];
        // No truthful action for this kind in this bucket: skip and say so
        // (e.g. inbox in "done", todo in "dismiss" which would reopen it).
        if (!action) { skipped += 1; continue; }
        try {
          // The row's own connection here too: at All a bulk run can hold rows
          // from three sections, and one anchor for all of them would silently
          // change nothing for two of the three.
          const at = it._site != null ? it._site : siteId;
          await api(`/research/todo-hub/resolve`, { method: "POST", body: JSON.stringify({ site_id: at, kind: it._kind, id: it._bid, action }) });
          ok += 1;
        } catch { failed += 1; failedIds.push(it.id); }
      }
      const parts = [`${ok} resolved`];
      if (skipped) parts.push(`${skipped} skipped (action not available for that item type)`);
      if (failed) parts.push(`${failed} failed`);
      setBulkNotice(parts.join(", "));
      // Failed items stay selected for a retry; a clean run exits select mode.
      if (failed) { setSel(failedIds); } else { setSel([]); setSelectMode(false); }
      await load();
    };

    const STATUSES = [["all", "All"], ["pending", "Pending"], ["ready", "Ready"], ["progress", "In progress"], ["done", "Done"]];
    const TYPES = [["all", "All"], ...Array.from(new Set(items.map(i => i.type))).map(t => [t, t])];
    const SOURCES = [["all", "All"], ["Competitors", "Competitors"], ["Partners", "Partners"], ["Industry", "Industry"], ["Products", "Products"], ["Inbox", "Inbox"], ["Research", "Research"], ["Manual", "Manual"]];
    const PRIOS = [["all", "All"], ["high", "High"], ["medium", "Medium"], ["low", "Low"], ["none", "None"]];
    const countBy = (key, getId) => { const c = { all: items.length }; items.forEach(i => { const k = getId(i); c[k] = (c[k] || 0) + 1; }); return c; };
    const statusCounts = countBy("status", i => i.status), typeCounts = countBy("type", i => i.type), sourceCounts = countBy("source", i => i.source), priCounts = countBy("pri", i => i.pri);
    const anyFilter = Object.values(f).some(v => v !== "all");

    const rows = items.filter(i =>
      (f.status === "all" || i.status === f.status) &&
      (f.type === "all" || i.type === f.type) &&
      (f.source === "all" || i.source === f.source) &&
      (f.pri === "all" || i.pri === f.pri));

    // Bulk-bar bookkeeping: what the current selection can truthfully receive,
    // so each button's label and disabled state match what will be sent.
    const selectableRows = rows.filter(canResolve);
    const selItems = items.filter(i => sel.includes(i.id));
    const nStart = selItems.filter(i => BULK_ACTION.progress[i._kind]).length;
    const nDismiss = selItems.filter(i => BULK_ACTION.dismiss[i._kind]).length;
    const nDone = selItems.filter(i => i._kind === "todo" || i._kind === "page").length;
    const nApprove = selItems.filter(i => i._kind === "topic" || i._kind === "keyword" || i._kind === "entity").length;
    // The resolve bucket must say what really happens: todos and pages are
    // marked done, pending topics/keywords/entities are APPROVED - a user
    // must never click "Mark done" and silently approve a keyword.
    const doneLabel = nDone && nApprove ? `Done ${nDone} + approve ${nApprove}` : nApprove ? `Approve (${nApprove})` : nDone ? `Mark done (${nDone})` : "Mark done";

    const renderActions = (it) => (
      <div className="research-cap-row" style={{ marginTop: viewMode === "list" ? 0 : 4 }} onClick={e => e.stopPropagation()}>
        {it.actions.map(a => { const [icon, label, variant] = ACTION[a]; return (
          <button key={a} className={"bi-btn bi-btn--sm" + (variant === "primary" ? " bi-btn--primary" : "")}
                  title={a === "open" && it.elsewhere ? it.openWhy : undefined}
                  onClick={() => resolve(it, a)}><I name={icon} size={14} />{label}</button>
        ); })}
        {itemErr[it.id] && <span style={{ fontSize: 11, color: "var(--danger)" }}>{itemErr[it.id]}</span>}
        {viewMode === "grid" && <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 4 }}>Details<I name="arrow-right" size={13} /></span>}
      </div>
    );

    const renderCard = (it) => {
      const card = (
        <div className="bi-card todo-card-clickable" onClick={() => setDetail(it)}>
          <div className="bi-card-head">
            <div className="bi-card__name" style={{ fontSize: "var(--text-sm)" }}>{it.title}</div>
            <div className="bi-card-badges">
              <span className="todo-type">{it.type}</span>
              <span className="todo-source"><I name={SOURCE_ICON[it.source]} size={11} />{it.source}</span>
              <span className={"research-prio " + PRIO_COLOR[it.pri]}>{it.pri}</span>
              {/* MODULE-SCOPE 1.1: at All every row names its section. It only
                  appears where the server sent one, which is the wide position:
                  at one section the column would repeat the same name on every
                  row. */}
              {it.section && <span className="todo-source"><I name="git-branch" size={11} />{it.section}</span>}
            </div>
          </div>
          {it.sub && <div className="todo-item__sub">{it.sub}</div>}
          {renderActions(it)}
        </div>
      );
      if (!selectMode) return <React.Fragment key={it.id}>{card}</React.Fragment>;
      // Non-resolvable kinds get a disabled checkbox: the resolver would 400
      // on them, so pretending they can be bulk-handled would only mislead.
      const canSel = canResolve(it);
      return <div key={it.id} className={"bi-select-wrap bi-select-wrap--on" + (sel.includes(it.id) ? " bi-select-wrap--selected" : "")}><label className="hds-check bi-select-wrap__check" onClick={e => e.stopPropagation()} title={canSel ? undefined : "This item type is handled from its own module, not from bulk actions"} style={canSel ? null : { opacity: 0.4, cursor: "not-allowed" }}><input type="checkbox" disabled={!canSel} checked={sel.includes(it.id)} onChange={() => canSel && toggle(it.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label>{card}</div>;
    };

    const renderRow = (it) => {
      const row = (
        <div className="bi-row todo-card-clickable" onClick={() => setDetail(it)}>
          <div className="bi-row__main" style={{ flexBasis: 280 }}><span className="bi-row__name">{it.title}</span>{it.sub && <span className="bi-row__url">{it.sub}</span>}</div>
          <div className="bi-row__badges"><span className="todo-type">{it.type}</span><span className="todo-source"><I name={SOURCE_ICON[it.source]} size={11} />{it.source}</span><span className={"research-prio " + PRIO_COLOR[it.pri]}>{it.pri}</span>{it.section && <span className="todo-source"><I name="git-branch" size={11} />{it.section}</span>}</div>
          <div className="bi-row__actions" onClick={e => e.stopPropagation()}>{it.actions.map(a => { const [icon, label, variant] = ACTION[a]; return <button key={a} className={"bi-btn bi-btn--sm" + (variant === "primary" ? " bi-btn--primary" : "")} title={a === "open" && it.elsewhere ? it.openWhy : undefined} onClick={() => resolve(it, a)}><I name={icon} size={14} />{label}</button>; })}{itemErr[it.id] && <span style={{ fontSize: 11, color: "var(--danger)" }}>{itemErr[it.id]}</span>}</div>
        </div>
      );
      if (!selectMode) return <React.Fragment key={it.id}>{row}</React.Fragment>;
      // Same gating as the cards: no bulk selection for kinds the resolver rejects.
      const canSel = canResolve(it);
      return <div key={it.id} className={"bi-select-wrap bi-select-wrap--on" + (sel.includes(it.id) ? " bi-select-wrap--selected" : "")}><label className="hds-check bi-select-wrap__check" onClick={e => e.stopPropagation()} title={canSel ? undefined : "This item type is handled from its own module, not from bulk actions"} style={canSel ? null : { opacity: 0.4, cursor: "not-allowed" }}><input type="checkbox" disabled={!canSel} checked={sel.includes(it.id)} onChange={() => canSel && toggle(it.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label>{row}</div>;
    };

    return (
      <div className="bi-page">
        <div className="bi-header">
          {/* The subtitle names what each list answers for, because the queue
              below is one connection's and the to-dos above it are the
              website's, and a customer at All must not read the first as the
              second. */}
          <div className="bi-header__titles"><h1 className="bi-title"><span className="bi-title__icon"><I name="check-square" size={20} /></span>To-do</h1><div className="bi-subtitle">Action hub · {whole ? "everything needing action across this website, with its own to-dos above it" : `everything needing action in ${(site && site.name) || "this section"}`}</div></div>
          <div className="bi-header-actions">
            <div className="bi-viewtoggle" role="tablist" aria-label="View mode">
              <button className={viewMode === "grid" ? "on" : ""} title="Grid view" onClick={() => setViewMode("grid")}><I name="grid" size={16} /></button>
              <button className={viewMode === "list" ? "on" : ""} title="List view" onClick={() => setViewMode("list")}><I name="list" size={16} /></button>
            </div>
            <button className="bi-btn" onClick={() => { setSelectMode(s => !s); setSel([]); setBulkNotice(""); }}>{selectMode ? <><I name="x" size={16} />Cancel</> : <><I name="check-square" size={16} />Select</>}</button>
            <button className="bi-btn" onClick={() => setShowForm(s => !s)}><I name="plus" size={16} />Add to-do</button>
            <button className="bi-btn" onClick={afterTodoWrite}><I name="refresh" size={16} />Refresh</button>
          </div>
        </div>

        {showForm && <TodoForm siteId={siteId} siteLabel={site && site.name} whole={whole} onClose={() => setShowForm(false)} onCreated={afterTodoWrite} />}

        {/* The website's own to-dos, at the switcher's position. One line, with
            the four counts on it, so the figures this module aggregates are
            visible without pushing the queue off the screen. */}
        <TodoList whole={whole} where={whereLabel} state={todoState} rows={todos} summary={todoSummary}
                  err={todoErr} summaryErr={todoSummaryErr} onRetry={loadTodos}
                  onStatus={setTodoStatus} onDelete={deleteTodo} rowErr={todoRowErr} />

        {/* Everything below belongs to the hub. Said once, here, rather than
            implied by a heading that does not exist: the filters, the bulk bar
            and the cards all act on it, and five of its eleven kinds answer for
            more than the connection the switcher is standing on. */}
        <ScopeNote scope={scope} whole={whole} filtered={anyFilter}
                   anchorName={(scope && scope.anchor && scope.anchor.name) || (site && site.name) || null} />

        <div className="bi-filterbar">
          <span className="bi-filterbar__label"><I name="filter" size={13} />Filter</span>
          <FilterPill label="Status" value={f.status} opts={STATUSES} counts={statusCounts} onPick={v => setFilter("status", v)} />
          <FilterPill label="Type" value={f.type} opts={TYPES} counts={typeCounts} onPick={v => setFilter("type", v)} />
          <FilterPill label="Source" value={f.source} opts={SOURCES} counts={sourceCounts} onPick={v => setFilter("source", v)} />
          <FilterPill label="Priority" value={f.pri} opts={PRIOS} counts={priCounts} onPick={v => setFilter("pri", v)} />
          {anyFilter && <button className="bi-clearall" onClick={() => setF({ status: "all", type: "all", source: "all", pri: "all" })}>Clear all</button>}
        </div>

        {selectMode && (
          <div className="bi-bulk-bar">
            <span className="bi-bulk-bar__count">{sel.length} selected</span>
            <button className="bi-btn bi-btn--sm" onClick={() => setSel(selectableRows.map(i => i.id))}><I name="check-square" size={14} />Select all ({selectableRows.length})</button>
            <button className="bi-btn bi-btn--sm" onClick={() => setSel([])}>Clear</button>
            <span className="bi-bulk-bar__sep" />
            <button className="bi-btn bi-btn--sm" disabled={!nStart} title="Only to-dos can be started" onClick={() => bulkResolve("progress")}><I name="play" size={14} />Start{nStart ? ` (${nStart})` : ""}</button>
            <button className="bi-btn bi-btn--sm" disabled={!nDone && !nApprove} title="To-dos and pages are marked done; topics, keywords and entities are approved; inbox items are skipped" onClick={() => bulkResolve("done")}><I name="check" size={14} />{doneLabel}</button>
            <button className="bi-btn bi-btn--sm bi-btn--danger" disabled={!nDismiss} title="To-dos cannot be dismissed from here (the resolver would reopen them); they are skipped" onClick={() => bulkResolve("dismiss")}><I name="x" size={14} />Dismiss{nDismiss ? ` (${nDismiss})` : ""}</button>
          </div>
        )}

        {bulkNotice && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text-muted)", margin: "6px 0" }}>
            <span>{bulkNotice}</span>
            <button className="bi-btn bi-btn--sm" onClick={() => setBulkNotice("")}><I name="x" size={13} />Hide</button>
          </div>
        )}

        {loading ? (
          <div className="bi-subempty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="todo-positive-empty">
            <span className="todo-positive-empty__icon"><I name="check-circle" size={30} /></span>
            <div className="todo-positive-empty__title">Nothing to do here</div>
            {/* An empty queue is a different sentence at each position, because
                "this website has nothing pending" and "this section has
                nothing pending" are different findings and only one of them
                is a reason to stop looking. */}
            <p className="todo-positive-empty__desc">{anyFilter
              ? "No items match these filters."
              : whole
                ? "Nothing is pending anywhere in this website: no reviews waiting, no captured gaps, no page targets undrafted, no analyses due."
                : `Nothing is pending in ${(site && site.name) || "this section"}. The whole website position gathers the to-dos, captured gaps, inbox items, page targets, topics and keywords of every section.`}</p>
          </div>
        ) : viewMode === "list" ? (
          <div className="bi-list--rows">{rows.map(renderRow)}</div>
        ) : (
          <div className="bi-list">{rows.map(renderCard)}</div>
        )}

        {/* The item's detail, in a drawer. Every field the hub knows about the
            item lives here, its actions act from here (closing on success,
            staying open on a failure so the error is read where the click
            happened), and jumping to the source module is a BUTTON, so the
            cost of a click on a card is a panel and never a navigation. */}
        {detail && (() => {
          const Drawer = window.HuddleDesignSystem_775113 && window.HuddleDesignSystem_775113.Drawer;
          const it = items.find(i => i.id === detail.id) || detail;
          const act = async (a) => { const ok = await resolve(it, a); if (ok) setDetail(null); };
          const body = (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
              <div className="bi-card-badges" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <span className="todo-type">{it.type}</span>
                <span className="todo-source"><I name={SOURCE_ICON[it.source]} size={11} />{it.source}</span>
                <span className={"research-prio " + PRIO_COLOR[it.pri]}>{it.pri}</span>
                <span className="todo-type">{it.status}</span>
                {it.section && <span className="todo-source"><I name="git-branch" size={11} />{it.section}</span>}
              </div>
              {it.sub && <div>{it.sub}</div>}
              {/* The referenced thing's own facts, per kind. A failed read is
                  said; hub metadata above stays either way. */}
              {extra && extra.loading && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Reading the details…</div>}
              {extra && extra.error && <div className="ds-strip ds-strip--error"><I name="alert-triangle" size={14} /><span>{extra.error}</span></div>}
              {extra && extra.data && it._kind === "entity" && (() => { const d = extra.data; const dd = (d && typeof d.data === "object" && d.data) || {}; return (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, background: "var(--bg-surface)", borderRadius: 8, padding: 12 }}>
                  {d.url && <div><a href={d.url} target="_blank" rel="noreferrer" style={{ color: "var(--info)" }}>{d.url}</a></div>}
                  {dd.summary && <div>{dd.summary}</div>}
                  {dd.market && <div style={{ fontSize: 12 }}><b>Market:</b> {dd.market}</div>}
                  {dd.positioning && <div style={{ fontSize: 12 }}><b>Positioning:</b> {dd.positioning}</div>}
                  {Array.isArray(dd.relationships_to_me) && dd.relationships_to_me.length > 0 && <div style={{ fontSize: 12 }}><b>Relationship:</b> {dd.relationships_to_me.join(", ")}</div>}
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Source: {d.source}{d.confidence != null ? ` · confidence ${d.confidence}%` : ""}</div>
                </div>
              ); })()}
              {extra && extra.data && it._kind === "keyword" && (() => { const d = extra.data; return (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, background: "var(--bg-surface)", borderRadius: 8, padding: 12, fontSize: 12 }}>
                  <div><b>Volume:</b> {d.volume != null ? d.volume : "unknown"}{d.intent ? ` · ${d.intent}` : ""}{d.cluster ? ` · cluster "${d.cluster}"` : ""}</div>
                  {d.notes && <div>{d.notes}</div>}
                </div>
              ); })()}
              {extra && extra.data && it._kind === "inbox" && (() => { const d = extra.data; return (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, background: "var(--bg-surface)", borderRadius: 8, padding: 12, fontSize: 12 }}>
                  {d.ai_summary ? <div>{d.ai_summary}</div> : d.raw_content ? <div>{String(d.raw_content).slice(0, 400)}{String(d.raw_content).length > 400 ? "…" : ""}</div> : <div>No content captured for this item.</div>}
                </div>
              ); })()}
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {it._kind === "todo" ? "A to-do on this website's own list. Mark it done or in progress here."
                  : it._kind === "profile" ? "Your business profile is missing details the AI runs lean on. Open it to fill them in."
                  : !canResolve(it) ? (it.resolveWhy || "This one is worked on in its own module.")
                  : it.nav ? `This item is worked on in ${it.source}. The actions here resolve it in place; the button below opens the module.`
                  : "This item resolves in place."}
              </div>
              {/* THE PER ROW VERB, where the row can say it at length
                  (MODULE-SCOPE 1.2). A row that sits in another section is
                  resolved where it sits, and its module is NOT offered, because
                  the modules open on the section the selector is standing on
                  and the button would arrive somewhere that cannot show it. */}
              {it.elsewhere && it.openWhy && (
                <div className="ds-strip" style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 12 }}>
                  <I name="git-branch" size={14} style={{ marginTop: 2, flexShrink: 0 }} /><span>{it.openWhy}</span>
                </div>
              )}
              {itemErr[it.id] && <div className="ds-strip ds-strip--error"><I name="alert-triangle" size={14} /><span>{itemErr[it.id]}</span></div>}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {it.actions.filter(a => a !== "open").map(a => { const [icon, label, variant] = ACTION[a]; return (
                  <button key={a} className={"bi-btn bi-btn--sm" + (variant === "primary" ? " bi-btn--primary" : "")} onClick={() => act(a)}><I name={icon} size={14} />{label}</button>
                ); })}
                {(it.nav || it._kind === "profile") && !it.elsewhere && (
                  <button className="bi-btn bi-btn--sm" onClick={() => { onNavigate && onNavigate(it.source, it._kind, it._bid); setDetail(null); }}>
                    <I name="arrow-up-right" size={14} />Open {it._kind === "profile" ? "Business Profile" : it._kind === "page" ? "Topics Bank" : it.source}
                  </button>
                )}
              </div>
            </div>
          );
          return Drawer
            ? <Drawer title={it.title} subtitle={`${it.type} from ${it.source}`} onClose={() => setDetail(null)}>{body}</Drawer>
            : <div className="bi-card" style={{ position: "fixed", right: 16, top: 72, width: 420, zIndex: 50 }}><div className="bi-card-head"><div className="bi-card__name">{it.title}</div><button className="bi-btn bi-btn--sm" onClick={() => setDetail(null)}><I name="x" size={14} /></button></div>{body}</div>;
        })()}
      </div>
    );
  }

  window.MlPipelineTodo = { TodoHub };
})();
